<script lang="ts" context="module">
  import { checkLegacyPermissionsAPI, requestCameraPermission } from "@/libs/camera"
  import { readable } from "svelte/store"

  const isLegacyPermissionsAPI = readable(false, (set) => {
    checkLegacyPermissionsAPI().then(set)
  })

  export enum SOURCE_OPTION {
    CAMERA = 1,
    UPLOAD,
    COMPANION,
    TUTORIAL,
  }
</script>

<script lang="ts">
  import { t } from "@/libs/i18n"
  import { fade } from "@/libs/ui"
  import { Button, KIND, SIZE } from "@/libs/ui/Button"
  import { FileUploader } from "@/libs/ui/FileUploader"
  import { ICON, Icon } from "@/libs/ui/Icon"
  import { getCameraPermissionStore } from "@/stores/camera-permission"
  import { createEventDispatcher, onMount, type ComponentProps } from "svelte"
  import TutorialBanner from "../tutorial/TutorialBanner.svelte"
  import CompanionInvite from "./companion/CompanionInvite.svelte"

  export let appConfig: ComponentProps<CompanionInvite>["appConfig"]
  export let options: SOURCE_OPTION[] = []
  export let tutorialOpen = false
  export let hasTutorial = false

  const cameraPermissionStore = getCameraPermissionStore()

  const dispatch = createEventDispatcher<{
    camera: void
    upload: File
    files: File[]
    tutorial: void
  }>()

  let isRequestingCameraPermission = false

  // Manually trigger camera permission request to update state for Safari users, see camera-permission.ts
  const updateCameraPermissionState = async () => {
    isRequestingCameraPermission = true
    const granted = await requestCameraPermission()
    isRequestingCameraPermission = false
    cameraPermissionStore.update({ expectedState: granted ? "granted" : "denied" })
    return granted
  }

  const handleCameraSelect = async () => {
    const granted = await updateCameraPermissionState()
    if (granted) dispatch("camera")
  }

  let isCompanionFlowStarted = false

  const handleCompanionStart = () => (isCompanionFlowStarted = true)
  const handleCompanionAbort = () => (isCompanionFlowStarted = false)

  $: hasCompanionOption = options.includes(SOURCE_OPTION.COMPANION)
  $: hasCameraOption = options.includes(SOURCE_OPTION.CAMERA)
  $: hasUploadOption = options.includes(SOURCE_OPTION.UPLOAD)
  $: hasTutorial = hasTutorial && hasCameraOption && hasUploadOption

  $: isCameraPermissionDenied = $cameraPermissionStore === "denied"
  $: isReloadRequiredForCameraPermission = $isLegacyPermissionsAPI

  const fadeInOptions = { duration: 350, delay: 350 }
  const fadeOutOptions = { duration: 350 }

  onMount(() => updateCameraPermissionState())
</script>

{#if hasTutorial && !tutorialOpen}
  <div class="absolute z-20 w-full bg-transparent p-4">
    <TutorialBanner on:click={() => dispatch("tutorial")} />
  </div>
{/if}

<section class="source-selection relative flex h-full w-full flex-col justify-between gap-6 p-4">
  {#if hasCompanionOption}
    <CompanionInvite
      {appConfig}
      on:start={handleCompanionStart}
      on:abort={handleCompanionAbort}
      on:done={({ detail: files }) => dispatch("files", files)}
      class="my-auto h-full max-h-[30rem]"
    />
  {:else if hasCameraOption && isCameraPermissionDenied}
    <article in:fade={fadeInOptions} out:fade={fadeOutOptions}>
      <div class="bg-[#EB5545]">
        <Icon type={ICON.CAMERA_SLASH} size={48} class="icon-camera-access-denied" />
      </div>
      <h2>{$t.source_selection.camera_permissions_warning.title}</h2>
      <p>
        {isReloadRequiredForCameraPermission
          ? $t.source_selection.camera_permissions_warning.instructions_reload
          : $t.source_selection.camera_permissions_warning.instructions_reset}
      </p>
    </article>
  {:else}
    <article class="prompt relative" in:fade={fadeInOptions} out:fade={fadeOutOptions}>
      {#if !hasTutorial}
        <div>
          <Icon type={ICON.CAMERA} size={48} class="icon-camera-lg" />
          <Icon type={ICON.PICTURE_2} size={48} class="icon-upload-lg" />
        </div>
      {/if}
      <h2 class:mt-10={hasTutorial}>{$t.source_selection.title}</h2>
    </article>
  {/if}
  {#if !isCompanionFlowStarted}
    <form class="flex w-full gap-2" class:horizontal={hasCompanionOption} on:submit|preventDefault>
      {#if hasCameraOption}
        <Button
          class="source-selection-button w-full"
          on:click={handleCameraSelect}
          disabled={isRequestingCameraPermission || isCameraPermissionDenied}
          kind={hasCompanionOption ? KIND.SECONDARY : KIND.PRIMARY}
          size={SIZE.MEDIUM}
        >
          <Icon type={ICON.CAMERA} class="flex-shrink-0" />
          <span>
            {hasCompanionOption
              ? $t.source_selection.continue_on_desktop
              : $t.source_selection.camera_video}
          </span>
        </Button>
      {/if}
      {#if hasUploadOption}
        <FileUploader
          class="source-selection-button w-full"
          disabled={isRequestingCameraPermission}
          size={SIZE.MEDIUM}
          kind={KIND.SECONDARY}
          on:upload={({ detail: file }) => dispatch("upload", file)}
        >
          <Icon type={ICON.PICTURE} class="flex-shrink-0" />
          {$t.source_selection.upload_photo}
        </FileUploader>
      {/if}
    </form>
  {/if}
</section>

<style lang="postcss">
  article:nth-child(n + 2) {
    position: absolute;
    top: 0;
  }

  :global(:not([data-ui-scale="lg"]):not([data-ui-scale="xl"])) form,
  form:not(.horizontal) {
    @apply flex-col;
  }

  :global([data-ui-scale="lg"]),
  :global([data-ui-scale="xl"]) {
    form.horizontal {
      @apply flex-row justify-center;

      :global(.source-selection-button) {
        @apply max-w-[16rem];
      }
    }
  }

  :global([data-ui-scale="lg"]) section,
  :global([data-ui-scale="xl"]) section {
    @apply p-10;
  }
  :global([data-ui-scale="xl"]) section {
    @apply mx-auto max-w-[42.5rem];
  }

  article {
    @apply my-auto flex flex-col items-center justify-center gap-2;

    & > div {
      @apply mb-4 h-16 w-16 overflow-hidden rounded-[18px] p-2 text-white;
    }
  }

  :global([data-ui-scale="lg"]) article > div,
  :global([data-ui-scale="xl"]) article > div {
    @apply mb-10;
  }

  .prompt > div {
    @apply flex animate-[6.5s_linear_infinite_gradient];

    & > :global(*) {
      @apply shrink-0 basis-full;
    }

    & > :global(:first-child) {
      @apply animate-[6.5s_linear_600ms_infinite_slide-1];
    }
    & > :global(:last-child) {
      @apply animate-[6.5s_linear_600ms_infinite_slide-2] opacity-0;
    }

    /**
     * Total time - 6500ms
     *
     * Gradient
     *  - green - 3000ms
     *  - green -> semi-transparent white  - 600ms
     *    - green -> transparent white - 350ms
     *    - transparent white -> semi-transparent white - 250ms
     *  - semi-transparent white - 2650ms
     *  - semi-transparent white -> green - 250ms
    */
    @keyframes gradient {
      0%, /* 0/6000 */
      46.15% /* 3000/6500 */ {
        background-color: #50d585;
      }
      52.53% /* 3350/6500 */ {
        background-color: rgba(var(--color) / 0.1);
      }
      55.38%, /* 3600/6500 */
      96.15% /* 6250/6500 */ {
        background-color: rgba(var(--color) / 0.2);
      }
      100% /* 6000/6000 */ {
        background-color: #50d585;
      }
    }

    /**
     * Total time - 6500ms
     *
     * Slide
     *  - delay 600ms
     *  - camera - 2400ms
     *  - camera -> gallery - 250ms
     *
     *  - gallery bounce - 600ms
     *  - gallery - 2400ms
     *  - gallery -> camera - 250ms
     *
     *  - camera bounce - 600ms
     *  - camera - 2400ms
     */
    @keyframes slide-1 {
      0%, /* 0/6000 */
      36.92%  /* 2400/6500 */ {
        transform: translateX(0);
        opacity: 1;
      }

      /* Slide forward */
      40.76% /* 2650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /** Bounce */
      42.30% /* 2750/6500 */ {
        transform: translateX(-114%);
        opacity: 1;
      }
      42.301% /* next tick */ {
        transform: translateX(-114%);
        opacity: 0;
      }
      46.15% /* 3000/6500 */ {
        transform: translateX(-93%);
        opacity: 0;
      }
      50%, /* 3250/6500 */
      86.92% /* 5650/6500 */ {
        transform: translateX(-100%);
        opacity: 0;
      }
      86.921% /* next tick */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /* Slide backward */
      90.76% /* 5900/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }
      /** Bounce */
      92.30% /* 6000/6500 */ {
        transform: translateX(14%);
        opacity: 1;
      }
      96.15% /* 6250/6500 */ {
        transform: translateX(-7%);
        opacity: 1;
      }
      100% /* 6000/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }
    }
    /**
     * Total time - 6500ms
     *
     * Slide
     *  - delay 600ms
     *  - camera - 2400ms
     *  - camera -> gallery - 250ms
     *
     *  - gallery bounce - 600ms
     *  - gallery - 2400ms
     *  - gallery -> camera - 250ms
     *
     *  - camera bounce - 600ms
     *  - camera - 2400ms
     */
    @keyframes slide-2 {
      0%, /* 0/6000 */
      36.92%  /* 2400/6500 */ {
        transform: translateX(0);
        opacity: 0;
      }
      36.921%  /* next tick */ {
        transform: translateX(0);
        opacity: 1;
      }

      /* Slide forward */
      40.76% /* 2650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /** Bounce */
      42.30% /* 2750/6500 */ {
        transform: translateX(-114%);
        opacity: 1;
      }
      46.15% /* 3000/6500 */ {
        transform: translateX(-93%);
        opacity: 1;
      }
      50%, /* 3250/6500 */
      86.92% /* 5650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /* Slide backward */
      90.76% /* 5900/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }

      /** Bounce */
      92.30% /* 6000/6500 */ {
        transform: translateX(14%);
        opacity: 1;
      }
      92.301% /* next tick */ {
        transform: translateX(14%);
        opacity: 0;
      }
      96.15% /* 6250/6500 */ {
        transform: translateX(-7%);
        opacity: 0;
      }
      100% /* 6000/6500 */ {
        transform: translateX(0%);
        opacity: 0;
      }
    }
  }

  h2 {
    @apply text-center;
  }

  p {
    @apply max-w-[30ch] text-center opacity-60;
  }

  :global([data-ui-scale="xs"]) p,
  :global([data-ui-scale="sm"]) p {
    @apply max-w-[25ch];
  }
  :global([data-ui-scale="lg"]) p,
  :global([data-ui-scale="xl"]) p {
    @apply max-w-[35ch];
  }
</style>
